import { PAYMENTS_ORDER_URL } from "../config/api";

export interface OrderItemRequest {
    stageId: string;
    quantity: number;
}

export interface InitOrderRequest {
    eventId: string;
    customerEmail: string;
    customerPhone: string;
    items: OrderItemRequest[];
    // SHA-256 (hex) of the secret this tab keeps for the tickets' single view (utils/purchaseSecret)
    purchaseSecretHash?: string;
}

export interface ProviderIntegrity {
    paymentPublicKey: string;
    integritySignature: string;
}

export interface MercadoPagoCheckout {
    paymentPublicKey: string;
    preferenceId: string;
    checkoutUrl: string;
}

// BUYER: the service fee is added to the tickets. ORGANIZER: the organizer pays it, the buyer pays only the tickets
export type FeePayer = 'BUYER' | 'ORGANIZER';

export interface OrderLine {
    stageId: string;
    stageName: string;
    quantity: number;
    unitPrice: number;
    // Service fee per ticket, VAT included
    unitServiceFee: number;
    subtotal: number;
    serviceFee: number;
}

// Tickets + SuperTicket service fee = total, computed by ms-payments-integration with the event's fee terms
export interface OrderQuote {
    eventId: string;
    currency: string;
    feePayer: FeePayer;
    items: OrderLine[];
    subtotal: number;
    serviceFee: number;
    total: number;
}

export interface InitOrderResponse {
    orderId: string;
    reservationId: string;
    status: string;
    expiresAt: string;
    // What the buyer pays (tickets + service fee): the amount sent to every gateway
    amount: number;
    currency: string;
    subtotal: number;
    serviceFee: number;
    feePayer: FeePayer;
    items: OrderLine[];
    boldIntegrity?: ProviderIntegrity;
    wompiIntegrity?: ProviderIntegrity;
    mercadoPagoCheckout?: MercadoPagoCheckout;
}

export class EventNotOnSaleError extends Error {
    constructor() {
        super('Las ventas de este evento están cerradas');
        this.name = 'EventNotOnSaleError';
    }
}

export type PaymentProvider = 'BOLD' | 'WOMPI' | 'MERCADOPAGO';

// Normalized across providers by ms-payments-integration
export type PaymentStatus = 'APPROVED' | 'PENDING' | 'REJECTED';

// REFUNDED: refunded by the organizer while under review, without tickets
export type OrderStatus = 'CREATED' | 'PENDING_PAYMENT' | 'PAID' | 'REJECTED' | 'EXPIRED' | 'CANCELLED' | 'REFUNDED';

// Normalized by ms-payments-integration from each gateway's own values
export type PaymentMethod = 'CREDIT_CARD' | 'DEBIT_CARD' | 'CARD' | 'PSE' | 'NEQUI' | 'DAVIPLATA' | 'BANCOLOMBIA'
    | 'QR' | 'BANK_TRANSFER' | 'MERCADO_PAGO_ACCOUNT' | 'CASH' | 'OTHER';

export interface OrderPaymentItem {
    stageId: string;
    // Absent for orders created before the service fee existed
    stageName?: string;
    quantity: number;
    unitPrice?: number;
    unitServiceFee?: number;
}

export interface OrderPaymentStatus {
    orderId: string;
    // Reservation ID, the reference sent to the payment gateway
    reference: string;
    eventId: string;
    eventName?: string;
    refundContactEmail?: string;
    refundContactPhone?: string;
    orderStatus: OrderStatus;
    paymentStatus: PaymentStatus;
    // The organizer must resolve the order (e.g. paid after it expired): no tickets until then
    underReview: boolean;
    provider?: PaymentProvider;
    transactionId?: string;
    // Of the paying transaction, or of the last attempt while unpaid; absent when no attempt is known
    paymentMethod?: PaymentMethod;
    // What the buyer pays
    amount: number;
    currency?: string;
    subtotal: number;
    serviceFee: number;
    feePayer?: FeePayer;
    items: OrderPaymentItem[];
    createdAt: string;
    paidAt?: string;
}

export class OrderNotFoundError extends Error {
    constructor() {
        super('La orden no existe');
        this.name = 'OrderNotFoundError';
    }
}

export const orderService = {
    // Price breakdown before buying; reserves nothing
    async quoteOrder(eventId: string, items: OrderItemRequest[]): Promise<OrderQuote> {
        const response = await fetch(`${PAYMENTS_ORDER_URL}/quote`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ eventId, items })
        });

        if (response.status === 409) {
            throw new EventNotOnSaleError();
        }
        if (!response.ok) {
            throw new Error('No fue posible calcular el valor de la compra');
        }

        return await response.json();
    },

    async initOrder(payload: InitOrderRequest): Promise<InitOrderResponse> {
        const response = await fetch(`${PAYMENTS_ORDER_URL}/init`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify(payload)
        });

        if (response.status === 409) {
            throw new EventNotOnSaleError();
        }
        if (!response.ok) {
            throw new Error('Error al inicializar la orden en el servidor');
        }

        return await response.json();
    },

    // The backend queries the gateway with the organizer's credentials and confirms the order.
    // transactionId: Wompi "id" / Mercado Pago "payment_id" from the return URL; Bold is queried by reference.
    async verifyOrder(reference: string, provider: PaymentProvider, transactionId?: string | null): Promise<OrderPaymentStatus> {
        const response = await fetch(`${PAYMENTS_ORDER_URL}/${encodeURIComponent(reference)}/verify`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ provider, transactionId: transactionId || undefined })
        });

        if (response.status === 404) {
            throw new OrderNotFoundError();
        }
        if (!response.ok) {
            throw new Error('Error al verificar el pago de la orden');
        }

        return await response.json();
    }
};